import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendEmailVerification,
  MultiFactorError,
  MultiFactorResolver,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';

// ── Beta Gate Toggle ──────────────────────────────────────────────────
// Set to `true` to re-enable beta access checks (checkBetaAccess, sign-in/up gating).
// When `false`, any authenticated user can access the app without a beta_access record.
export const BETA_GATE_ENABLED = false;

// Beta access check response
interface BetaAccessResponse {
  hasAccess: boolean;
  status?: 'invited' | 'active' | 'suspended' | 'revoked';
  cohort?: string;
  message?: string;
}

// Activation response
interface ActivateBetaUserResponse {
  success: boolean;
  message?: string;
}

// Custom error for beta access denial
export class BetaAccessDeniedError extends Error {
  constructor(public email: string, message?: string) {
    super(message || 'Beta access required');
    this.name = 'BetaAccessDeniedError';
  }
}

// Custom error for suspended/revoked access
export class BetaAccessRevokedError extends Error {
  constructor(public email: string, message?: string) {
    super(message || 'Your beta access has been revoked or suspended.');
    this.name = 'BetaAccessRevokedError';
  }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  betaAccessDenied: boolean;
  authError: string | null;
  mfaRequired: boolean;
  mfaResolver: MultiFactorResolver | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkBetaAccess: (email: string) => Promise<BetaAccessResponse>;
  resolveMfaSignIn: (verificationId: string, verificationCode: string) => Promise<void>;
  clearMfaState: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [betaAccessDenied, setBetaAccessDenied] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser && authUser.email && BETA_GATE_ENABLED) {
        // Check beta access for logged-in users
        try {
          const checkAccess = httpsCallable<{ email: string }, BetaAccessResponse>(
            functions,
            'checkBetaAccessHttp'
          );
          const result = await checkAccess({ email: authUser.email.toLowerCase() });

          if (!result.data.hasAccess) {
            // User's access has been revoked or suspended
            console.warn('Beta access denied for user:', authUser.email);
            setBetaAccessDenied(true);
            await firebaseSignOut(auth);
            setUser(null);
            setLoading(false);
            return;
          }

          // Auto-activate users still in "invited" status (self-healing)
          if (result.data.status === 'invited') {
            try {
              const activate = httpsCallable<{ betaAgreementAccepted: boolean }, ActivateBetaUserResponse>(
                functions,
                'activateBetaUserHttp'
              );
              await activate({ betaAgreementAccepted: true });
            } catch (activateError) {
              console.error('Failed to auto-activate beta user:', activateError);
            }
          }

          setBetaAccessDenied(false);
          setAuthError(null);
        } catch (error) {
          // FAIL-CLOSED: Do not grant access when beta check fails
          console.warn('Beta access check failed, denying access:', error);
          setBetaAccessDenied(true);
          setAuthError('Unable to verify beta access. Please check your connection and try again.');
          await firebaseSignOut(auth);
          setUser(null);
          setLoading(false);
          return;
        }
      } else {
        setBetaAccessDenied(false);
      }

      setUser(authUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const checkBetaAccess = async (email: string): Promise<BetaAccessResponse> => {
    const checkAccess = httpsCallable<{ email: string }, BetaAccessResponse>(
      functions,
      'checkBetaAccessHttp'
    );
    const result = await checkAccess({ email: email.toLowerCase() });
    return result.data;
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    let accessStatus: string | undefined;

    // Check beta access before sign in (bypassed when BETA_GATE_ENABLED = false)
    if (BETA_GATE_ENABLED) {
      try {
        const accessResult = await checkBetaAccess(email);

        if (!accessResult.hasAccess) {
          if (accessResult.status === 'suspended' || accessResult.status === 'revoked') {
            throw new BetaAccessRevokedError(email, accessResult.message);
          }
          throw new BetaAccessDeniedError(email, accessResult.message);
        }

        accessStatus = accessResult.status;
      } catch (error) {
        if (error instanceof BetaAccessDeniedError || error instanceof BetaAccessRevokedError) {
          throw error;
        }
        // FAIL-CLOSED: Do not allow sign-in when beta check fails
        console.warn('Beta access check failed, denying sign-in:', error);
        throw new Error('Unable to verify beta access. Please check your connection and try again.');
      }
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      // Check if MFA is required
      if (error?.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
        setMfaResolver(resolver);
        setMfaRequired(true);
        // Don't re-throw — the UI will show the MFA challenge screen
        return;
      }
      throw error;
    }

    // If user status is "invited", activate them now that they've signed in
    if (BETA_GATE_ENABLED && accessStatus === 'invited') {
      try {
        const activate = httpsCallable<{ betaAgreementAccepted: boolean }, ActivateBetaUserResponse>(
          functions,
          'activateBetaUserHttp'
        );
        await activate({ betaAgreementAccepted: true });
      } catch (error) {
        console.error('Failed to activate beta user on sign in:', error);
      }
    }
  };

  const signUp = async (email: string, password: string): Promise<void> => {
    // Step 1: Check beta access BEFORE creating account (bypassed when BETA_GATE_ENABLED = false)
    if (BETA_GATE_ENABLED) {
      const accessResult = await checkBetaAccess(email);

      if (!accessResult.hasAccess) {
        throw new BetaAccessDeniedError(email, accessResult.message);
      }
    }

    // Step 2: Create Firebase Auth account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Step 3: Send email verification
    try {
      await sendEmailVerification(userCredential.user);
    } catch (verifyError) {
      // Log but don't block — the EmailVerification page can resend
      console.error('Failed to send initial verification email:', verifyError);
    }

    // Step 4: Activate beta user.
    //
    // BUG-108: This runs regardless of BETA_GATE_ENABLED. It is now
    // belt-and-suspenders — the server-side `beforeUserCreated` trigger is the
    // guarantee that `/users/{uid}` exists; this call layers the
    // agreement/activation fields on top. We do NOT gate it behind the flag,
    // because provisioning must never depend on a flag or an abandonable step.
    //
    // The account already exists by this point, so a failure here never blocks
    // signup — but it is surfaced (logged at error, not silently swallowed) so
    // real failures are visible. A `permission-denied` is expected while the
    // beta gate config (beta_config.gateStatus) is restrictive and the email
    // has no beta_access record; that is harmless now that the trigger
    // provisions the baseline doc.
    //
    // NOTE for Emily: whether the beta should be gated (the value/meaning of
    // BETA_GATE_ENABLED and beta_config.gateStatus) is a separate product
    // decision and is intentionally NOT changed here.
    try {
      const activate = httpsCallable<{ betaAgreementAccepted: boolean }, ActivateBetaUserResponse>(
        functions,
        'activateBetaUserHttp'
      );
      await activate({ betaAgreementAccepted: true });
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (code === 'functions/permission-denied') {
        // Expected when the gate config is restrictive and there is no
        // beta_access record. Baseline doc is still guaranteed by the trigger.
        console.info(
          'Beta activation skipped (gate config restrictive); baseline user doc is provisioned server-side.'
        );
      } else {
        // Real, unexpected failure — surface it. Non-fatal: the account exists
        // and the server trigger has provisioned the baseline user doc.
        console.error('Beta activation failed after signup (non-fatal):', error);
      }
    }
  };

  const signOut = async (): Promise<void> => {
    setMfaRequired(false);
    setMfaResolver(null);
    setAuthError(null);
    await firebaseSignOut(auth);
  };

  const resolveMfaSignIn = async (verificationId: string, verificationCode: string): Promise<void> => {
    if (!mfaResolver) {
      throw new Error('No MFA resolver available');
    }

    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    await mfaResolver.resolveSignIn(multiFactorAssertion);

    // Clear MFA state after successful verification
    setMfaRequired(false);
    setMfaResolver(null);
  };

  const clearMfaState = (): void => {
    setMfaRequired(false);
    setMfaResolver(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    betaAccessDenied,
    authError,
    mfaRequired,
    mfaResolver,
    signIn,
    signUp,
    signOut,
    checkBetaAccess,
    resolveMfaSignIn,
    clearMfaState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
